import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { ToastType } from '@corratech/pylot-ui/context'
import getStockValidation from '@pylot-data/api/operations/get-stock-validation'
import { useCart, isGiftCardMixCartResult } from '@corratech/pylot-cart-manager'
import { BundleItem } from '@pylot-data/fwrdschema'
import { cartUpdateEvent } from '@lib/utils/exponeaTrackingEvents'
import { useAddToCart } from '@pylot-data/hooks/cartManager/use-add-to-cart'
import { PDP_REVISIT_EVENT } from './pdpEvents'

type AddToCartFn = ReturnType<typeof useAddToCart>['addToCart']

type UsePdpAddToCartArgs = {
    isBundle: boolean
    product: any
    variant: any
    selectedOptionUIDs: string[]
    quantity: number
    setQuantity?: (qty: number) => void // OPTIONAL: pass to auto-clamp
    productUrl?: string
    addToCart: AddToCartFn

    t: (key: string, vars?: Record<string, any>) => string
    isMobile: boolean
    openSidebar: () => void
    closeSidebar: () => void
    openToast: (msg: string, type: ToastType, sticky?: boolean) => void
    openCartToast: (msg: string, type: ToastType) => void
    timeoutAfterOpeningSidebar?: number
}

export function usePDPAddToCart({
    isBundle,
    product,
    variant,
    selectedOptionUIDs,
    quantity,
    setQuantity,
    productUrl,
    addToCart,
    t,
    isMobile,
    openSidebar,
    closeSidebar,
    openToast,
    openCartToast,
    timeoutAfterOpeningSidebar = 50
}: UsePdpAddToCartArgs) {
    const [isAddingToCart, setIsAddingToCart] = useState(false)
    const [availableStock, setAvailableStock] = useState<number | null>(null)
    const [stockValidated, setStockValidated] = useState(false)
    const [stockBySku, setStockBySku] = useState<Record<string, number>>({})

    const productNameForMessage = product?.name ?? ''
    const noStockMessage = t(
        'common||There is not enough stock to continue adding to cart.'
    )
    const infoMessageWithMaxStock = (maxQty: number) =>
        t(
            'common||The product {{name}} does not have enough stock. The maximum available quantity is {{qty}}.',
            { name: productNameForMessage, qty: maxQty }
        )

    const inFlightRef = useRef(false)

    const resetStockState = useCallback(() => {
        setAvailableStock(null)
        setStockValidated(false)
        setStockBySku({})
    }, [])

    useEffect(() => {
        resetStockState()
    }, [productUrl, resetStockState])

    useEffect(() => {
        const handlePdpRevisit = () => resetStockState()

        window.addEventListener(PDP_REVISIT_EVENT, handlePdpRevisit)
        return () =>
            window.removeEventListener(PDP_REVISIT_EVENT, handlePdpRevisit)
    }, [resetStockState])

    const { data } = useCart()
    const cartItems = data?.data?.cart?.items ?? []

    const skuToValidate = useMemo(() => {
        if (!product?.sku) return variant?.sku
        return variant?.sku && variant.sku !== product.sku
            ? variant.sku
            : product.sku
    }, [product?.sku, variant?.sku])

    const getQtyAlreadyInCart = useCallback(
        (sku: string): number => {
            if (!sku) return 0

            return cartItems.reduce((acc: number, item: any) => {
                // simple
                const simpleSku = item?.sku || item?.product?.sku
                if (simpleSku === sku) return acc + Number(item.quantity)

                // configurable child sku
                if (item?.configurable_options?.length) {
                    const childSku = item.configurable_options[0]?.sku_child
                    if (childSku === sku) return acc + Number(item.quantity)
                }

                return acc
            }, 0)
        },
        [cartItems]
    )

    const qtyAlreadyInCart = useMemo(
        () => (skuToValidate ? getQtyAlreadyInCart(skuToValidate) : 0),
        [skuToValidate, getQtyAlreadyInCart]
    )

    const availableStockForInput = useMemo(() => {
        if (availableStock === null) return null
        return Math.max(availableStock - qtyAlreadyInCart, 0)
    }, [availableStock, qtyAlreadyInCart])

    const isOutOfStockByQty = useMemo(() => {
        if (availableStock === null) return false
        return quantity + qtyAlreadyInCart > availableStock
    }, [availableStock, quantity, qtyAlreadyInCart])

    useEffect(() => {
        if (!variant?.sku) return
        const cachedStock = stockBySku[variant.sku]
        if (cachedStock !== undefined) {
            setAvailableStock(cachedStock)
            setStockValidated(true)
        } else {
            setAvailableStock(null)
            setStockValidated(false)
        }
    }, [variant?.sku, stockBySku])

    useEffect(() => {
        if (!setQuantity) return
        if (availableStockForInput === null) return
        if (quantity > availableStockForInput)
            setQuantity(availableStockForInput)
    }, [availableStockForInput, quantity, setQuantity])

    const addToCartHandler = useCallback(async () => {
        if (inFlightRef.current) return
        inFlightRef.current = true
        setIsAddingToCart(true)

        try {
            const stockValidationSku = isBundle
                ? ((product?.items
                      ?.map((item: BundleItem) => {
                          const itemSku = item.options?.[0]?.product?.sku
                          const itemQtyPerBundle = Number(
                              item.options?.[0]?.quantity ?? 1
                          )
                          if (!itemSku) return null
                          return {
                              sku: itemSku,
                              quantity: String(itemQtyPerBundle * quantity)
                          }
                      })
                      .filter(Boolean) as {
                      sku: string
                      quantity: string
                  }[]) ?? [])
                : [{ sku: variant.sku, quantity: quantity.toString() }]

            const { getValidationStock: beforePlaceOrderStockValidation } =
                await getStockValidation({
                    input: { items: stockValidationSku }
                })

            const totalStock = isBundle
                ? beforePlaceOrderStockValidation.reduce(
                      (min: number, item: any, index: number) => {
                          const bundleQtyPerItem = Number(
                              product.items?.[index]?.options?.[0]?.quantity ??
                                  1
                          )
                          const availableUnits = Number(item.stock ?? 0)
                          const setsAvailable = Math.floor(
                              availableUnits / bundleQtyPerItem
                          )
                          return Math.min(min, setsAvailable)
                      },
                      Infinity
                  )
                : Number(beforePlaceOrderStockValidation?.[0]?.stock ?? 0)

            const stock = Number(totalStock)

            if (variant?.sku)
                setStockBySku((prev) => ({ ...prev, [variant.sku]: stock }))
            setAvailableStock(stock)
            setStockValidated(true)

            const skuCheck = skuToValidate ?? variant?.sku ?? product?.sku
            const alreadyInCart = skuCheck ? getQtyAlreadyInCart(skuCheck) : 0

            const totalRequestedQty = alreadyInCart + quantity
            const exceedsAvailableStock = totalRequestedQty > stock
            const remainingStock = stock - alreadyInCart
            const adjustedQty = Math.max(remainingStock, 0)

            if (exceedsAvailableStock && remainingStock <= 0) {
                openToast(noStockMessage, ToastType.Warning, true)
                return
            }

            const bundleOptions =
                isBundle &&
                product?.items?.map(
                    (item: BundleItem) =>
                        item.options?.length && item.options[0]?.uid!
                )

            const addToCartOptions = isBundle
                ? bundleOptions
                : selectedOptionUIDs

            const response = await addToCart(
                [
                    {
                        sku: variant.sku,
                        uid: variant.uid,
                        ...(variant.sku !== product.sku && {
                            parent_sku: product.sku
                        }),
                        selected_options: addToCartOptions as any,
                        quantity: exceedsAvailableStock ? adjustedQty : quantity
                    }
                ],
                [product]
            )

            const errorMessage =
                response?.errors?.[0]?.message ??
                response?.user_errors?.[0]?.message ??
                null

            const successMessage = t('cart|{{name}} was added to cart', {
                name: product?.name
            })

            if (isGiftCardMixCartResult(response)) {
                return
            }

            if (!isMobile && !errorMessage) {
                openSidebar()
                setTimeout(() => closeSidebar(), 4000)
            } else if (errorMessage) {
                openSidebar()
            }

            if (response?.data && !errorMessage) {
                const { cart } = response.data
                const addedProduct = {
                    ...product,
                    sku: variant.sku,
                    uid: variant.uid
                }
                cartUpdateEvent('add', addedProduct, cart)
            }

            const toastMsg = exceedsAvailableStock
                ? infoMessageWithMaxStock(stock)
                : errorMessage
                  ? errorMessage
                  : successMessage

            const toastType =
                exceedsAvailableStock || errorMessage
                    ? ToastType.Warning
                    : ToastType.Success

            setTimeout(() => {
                if (isMobile && !errorMessage)
                    openToast(toastMsg, toastType, true)
                else openCartToast(toastMsg, toastType)
            }, timeoutAfterOpeningSidebar)
        } finally {
            setIsAddingToCart(false)
            inFlightRef.current = false
        }
    }, [
        isBundle,
        product,
        variant,
        quantity,
        selectedOptionUIDs,
        addToCart,
        t,
        isMobile,
        openSidebar,
        closeSidebar,
        openToast,
        openCartToast,
        timeoutAfterOpeningSidebar,
        skuToValidate,
        getQtyAlreadyInCart
    ])

    return {
        addToCartHandler,
        isAddingToCart,
        availableStock,
        availableStockForInput,
        isOutOfStockByQty,
        stockValidated
    }
}
